import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import Sidebar from '../../../components/Sidebar';
import Header from '../../../components/Header';
import { useSelector, useDispatch } from 'react-redux';
import { useState } from 'react';
import { Layout, List } from 'react-feather';
import { Button, DatePicker, Spin, Tabs, notification } from 'antd';
import './overview.css';
import TimelineScheduler from './TimelineScheduler';
import dayjs from 'dayjs';
import FeatherIcon from 'feather-icons-react';
import AdvancedTable from '../../../components/AdvancedTable';
import { useNavigate } from 'react-router-dom';
import { matchPermissions } from '../../../utils/permissionHelper';
import { openPrintPreview } from '../../../components/Print/PrintPreview';
import { PERMISSIONS } from '../../../constants';

import { getBookingStats, getBookingColorMap } from './utils';
import {
  getBaseColumns,
  getMediDaycareColumns,
  getCheckInColumns,
  getInHouseColumns,
  getCheckOutColumns
} from './columns';
import { ActionUpdateServiceSlot } from '../../../store/Actions/ServiceSlotAction';
import useRoomDashboardFilters from './hooks/useRoomDashboardFilters';
import useRoomDashboardData from './hooks/useRoomDashboardData';
import DailyRoomStatus from './DailyRoomStatus';

const RoomDashboard = () => {
  const { userDetails } = useSelector(state => state?.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [modal, setModal] = useState({ is: false, id: '' });
  const matchPermissionsForUser = matchPermissions(userDetails);

  // Custom Hooks
  const {
    activeTab,
    setActiveTab,
    dashboardDate,
    setDashboardDate,
    timelineRange,
    setTimelineRange,
    updateUrl
  } = useRoomDashboardFilters();

  const {
    roomDashboard,
    checkIns,
    checkOuts,
    inHouse,
    mediDaycareCount,
    loader,
    fetchDashboardData,
    fetchCheckInsData,
    fetchCheckOutsData,
    fetchMediDaycareCheckIn,
    fetchInHouseData
  } = useRoomDashboardData({
    dashboardDate,
    timelineRange,
    activeTab,
    updateUrl,
    refreshTrigger
  });

  /* ---------------- HANDLERS ---------------- */

  const handleDashboardDateChange = (_, dateString) => {
    if (!dateString) return;
    if (dashboardDate === dateString) {
      fetchDashboardData(dateString);
    } else {
      setDashboardDate(dateString);
    }
  };

  const handleTimelineDateRangeChange = (dates) => {
    if (!dates) {
      setTimelineRange({ startDate: null, endDate: null });
      return;
    }
    setTimelineRange({
      startDate: dates[0]?.format('YYYY-MM-DD'),
      endDate: dates[1]?.format('YYYY-MM-DD'),
    });
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const handleCheckIn = (record) => {
    if (record.patientId?.id)
      navigate(`/check-in/${record?.orderId}?bookingId=${record.id}&roomName=${record?.currentRoom?.roomId?.roomNo}`);
  };

  const handleMediDaycareCheckIn = (record) => {
    navigate(`/indoor-casepaper/${record?.customerId?._id}?orderId=${record?.orderId}&patientPlanId=${record?.patientPlan?.[0]?._id}`);
  };

  const handleCheckOut = (record) => {
    navigate(`/checkout/${(record?._id || record?.id)}/${(record?.orderId?._id || record?.orderId)}`);
  };
  const handleInHouseForm = (record) => {
    const roomName = record?.currentRoom?.roomId?.roomNo || '';
    const roomType = record?.currentRoom?.roomTypeId?.name?.en || record?.currentRoom?.roomTypeId?.name || '';
    navigate(`/indoor-casepaper/${record?.patientId?._id}?orderId=${record?.orderId?._id}&patientPlanId=${record?.patientPlanId}&roomName=${roomName}&roomType=${roomType}`);
  };

  const handleStatusUpdate = (recordId, newStatus) => {
    dispatch(
      ActionUpdateServiceSlot(
        {
          Query: recordId,
          obj: { status: newStatus },
        },
        () => setRefreshTrigger(prev => prev + 1)
      )
    );
  };


  const handleDownloadDietPlan = () => {
    if (!inHouse || inHouse.length === 0) {
      notification.warning({
        message: 'No Data Available',
        description: `No in-house guests found for ${dayjs(dashboardDate).format('DD MMM YYYY')}.`,
        placement: 'topRight',
      });
      return;
    }

    // Define Headers
    const headers = [
      'Guest Name',
      'Room No',
      'Room Type',
      'Diet Plan Note',
      'Diet Plan (Today)'
    ];
    // Format rows
    const tableData = inHouse.map(row => {
      const guestName = row.patientId?.name || 'N/A';
      const roomNo = row.currentRoom?.roomId?.roomNo || 'N/A';
      const roomType = row.currentRoom?.roomTypeId?.name?.en || row.currentRoom?.roomTypeId?.name || 'N/A';
      const diet = row.dietPlan?.[dashboardDate] || 'Not Assigned';
      const dietPlanNote = row.dietPlanNote || 'N/A';

      return [
        guestName,
        roomNo,
        roomType,
        dietPlanNote,
        diet
      ];
    });

    // Open Print Preview
    openPrintPreview({}, `Diet Plan Summary - ${dayjs(dashboardDate).format('DD MMM YYYY')}`, {
      sections: [{
        title: `In-House Guests (${inHouse.length})`,
        type: 'table',
        headers: headers,
        data: tableData
      }]
    });

    notification.success({
      message: 'Print Preview Opened',
      description: 'Please check the new window/tab to print or save as PDF.',
      placement: 'topRight',
    });
  };

  const handleDownloadTreatmentPlan = () => {
    if (!inHouse || inHouse.length === 0) {
      notification.warning({
        message: 'No Data Available',
        description: `No in-house guests found for ${dayjs(dashboardDate).format('DD MMM YYYY')}.`,
        placement: 'topRight',
      });
      return;
    }

    // Filter patients who have treatment plans for the selected date
    const patientsWithTreatment = inHouse.filter(patient => {
      const serviceDetails = patient.serviceDetails?.[dashboardDate];
      return serviceDetails && serviceDetails.length > 0;
    });

    if (patientsWithTreatment.length === 0) {
      notification.warning({
        message: 'No Treatment Plans',
        description: `No treatment plans found for ${dayjs(dashboardDate).format('DD MMM YYYY')}.`,
        placement: 'topRight',
      });
      return;
    }

    // Build sections for each patient
    const sections = [];
    console.log({ patientsWithTreatment });

    patientsWithTreatment.forEach((patient, index) => {
      const patientName = patient.patientId?.name || 'Unknown Patient';
      const roomNo = patient.currentRoom?.roomId?.roomNo || 'N/A';
      const roomType = patient.currentRoom?.roomTypeId?.name?.en || patient.currentRoom?.roomTypeId?.name || 'N/A';
      const services = patient.serviceDetails?.[dashboardDate] || [];

      // Patient Info Section
      sections.push({
        title: `Patient ${index + 1}: ${patientName}`,
        fields: [
          { label: 'Room Number', value: roomNo },
          { label: 'Room Type', value: roomType },
          { label: 'Admission Date', value: patient.admissionDate ? dayjs(patient.admissionDate).format('DD MMM YYYY') : 'N/A' },
          { label: 'Discharge Date', value: patient.dischargeDate ? dayjs(patient.dischargeDate).format('DD MMM YYYY') : 'N/A' },
        ]
      });

      // Treatment Schedule Table
      const treatmentHeaders = ['Service Name', 'Time Slot', 'Duration (min)', 'Medicines'];
      const treatmentData = services.map(service => {
        const startTime = service.startTime || 'N/A';
        const endTime = service.endTime || 'N/A';
        const timeSlot = `${startTime} - ${endTime}`;
        const duration = service.totalMinutes || 'N/A';

        // Format medicines - show the selected medicine value
        const medicinesText = service.medicineValue || 'Not Assigned';

        return [
          service.serviceName || 'N/A',
          timeSlot,
          duration,
          medicinesText
        ];
      });

      sections.push({
        title: `Treatment Schedule - ${dayjs(dashboardDate).format('DD MMM YYYY')}`,
        type: 'table',
        headers: treatmentHeaders,
        data: treatmentData
      });

      // Add spacing between patients
      if (index < patientsWithTreatment.length - 1) {
        sections.push({
          title: '',
          fields: [{ label: '', value: '---' }]
        });
      }
    });

    // Open Print Preview
    const fileName = `Treatment_Plan_${dayjs(dashboardDate).format('DD-MMM-YYYY')}`;
    openPrintPreview(
      {},
      fileName,
      {
        sections: sections,
        headerConfig: {
          hospitalName: 'Prakruti Ayurvedic Health Center & Hospital',
          subtitle: `Treatment Plan Summary - ${dayjs(dashboardDate).format('DD MMMM YYYY')}`,
          logo: '/prakruti-logo.png'
        }
      }
    );

    notification.success({
      message: 'Print Preview Opened',
      description: `Treatment plans for ${patientsWithTreatment.length} patient(s) are ready to print or save as PDF.`,
      placement: 'topRight',
    });
  };


  const totalRoomCount = roomDashboard?.totalRoomCount;
  const totalNonSharedRoomCount = roomDashboard?.nonSharedBookings;
  const totalSharedRoomCount = roomDashboard?.sharedBookings;
  const bookingStats = getBookingStats(inHouse);
  const bookingColorMap = getBookingColorMap(inHouse);

  const canUpdateService = matchPermissionsForUser('service-slot.update');
  const canViewStats = matchPermissionsForUser(PERMISSIONS.ROOMS.STATS_VIEW);
  const canViewInHouse = matchPermissionsForUser(PERMISSIONS.ROOMS.INHOUSE_VIEW);
  const canViewCheckIn = matchPermissionsForUser(PERMISSIONS.ROOMS.CHECKIN_VIEW);
  const canViewCheckOut = matchPermissionsForUser(PERMISSIONS.ROOMS.CHECKOUT_VIEW);
  const canViewMediDaycare = matchPermissionsForUser(PERMISSIONS.ROOMS.MEDI_DAYCARE_VIEW);
  const canViewScheduler = matchPermissionsForUser(PERMISSIONS.ROOMS.SCHEDULER_VIEW);
  const canDownloadDietPlan = matchPermissionsForUser(PERMISSIONS.ROOMS.DOWNLOAD_DIET_PLAN);
  const canDownloadTreatmentPlan = matchPermissionsForUser(PERMISSIONS.ROOMS.DOWNLOAD_TREATMENT_PLAN);

  const baseColumns = getBaseColumns(bookingStats, bookingColorMap, dashboardDate);
  const mediDaycareCheckInColumns = getMediDaycareColumns(handleMediDaycareCheckIn, handleStatusUpdate, canUpdateService);
  const checkInColumns = getCheckInColumns(baseColumns, handleCheckIn);
  const inHouseColumns = getInHouseColumns(baseColumns, dashboardDate, handleInHouseForm, handleCheckOut);
  const checkOutColumns = getCheckOutColumns(baseColumns, handleCheckOut);

  // Data fetchers for AdvancedTable now come from useRoomDashboardData hook

  const handleCloseModal = () => {
    setModal({ is: false, id: '' });
    setRefreshTrigger(prev => prev + 1);
  };

  /* Custom Tab Titles */
  const tabDashboard = (
    <div className="tab-pill-content">
      <Layout size={18} />
      <span>Dashboard</span>
    </div>
  );

  const tabTimeline = (
    <div className="tab-pill-content">
      <List size={18} />
      <span>Scheduler</span>
    </div>
  );

  return (
    <>
      <Header />
      <Sidebar />
      <div className="room-dashoard-home">
        <div className={`page-wrapper ${loader ? 'loader-active' : ''}`}>
          {loader && <div className="loader-overlay"></div>}
          <div className="p-4 mt-3">
            <Tabs
              defaultActiveKey="1"
              activeKey={activeTab}
              onChange={handleTabChange}
              size="large"
              className="professional-tabs"
              destroyInactiveTabPane
            >
              <Tabs.TabPane tab={tabDashboard} key="1">
                <div className="dashboard-overview-container">
                  {/* Dedicated Dashboard Header */}
                  <div className="dashboard-header-row">
                    <div className="d-flex align-items-center gap-3 col-lg-6">
                      <DatePicker
                        value={dashboardDate ? dayjs(dashboardDate) : null}
                        onChange={handleDashboardDateChange}
                        placeholder="Select Date"
                        style={{ width: '100%', height: '42px', maxWidth: '300px' }}
                        allowClear={false}
                      />
                      <Button
                        type="text"
                        className="clear-btn"
                        onClick={() => setDashboardDate(dayjs().format('YYYY-MM-DD'))}
                        icon={<FeatherIcon icon="rotate-ccw" size={14} />}
                      >
                        Today
                      </Button>
                    </div>

                    <div className="dashboard-actions">
                      {canDownloadDietPlan && (
                        <Button
                          type="primary"
                          icon={<FeatherIcon icon="download" size={16} />}
                          onClick={handleDownloadDietPlan}
                          className="d-flex align-items-center gap-2"
                          size="large"
                        >
                          Download Diet Plan
                        </Button>
                      )}
                      {canDownloadTreatmentPlan && (
                        <Button
                          type="primary"
                          icon={<FeatherIcon icon="file-text" size={16} />}
                          onClick={handleDownloadTreatmentPlan}
                          className="d-flex align-items-center gap-2"
                          size="large"
                          style={{ marginLeft: '12px' }}
                        >
                          Download Treatment Plan
                        </Button>
                      )}
                    </div>
                  </div>


                  {/* Tables Segmented View */}
                  <div className="section-card mt-3  ">
                    <Tabs defaultActiveKey="room-layout" className="sub-tabs-container">
                      <Tabs.TabPane
                        tab={
                          <div className="sub-tab-title">
                            <FeatherIcon icon="grid" size={16} />
                            <span>Room Status</span>
                          </div>
                        }
                        key="room-layout"
                      >
                        <DailyRoomStatus dashboardDate={dashboardDate} />
                      </Tabs.TabPane>

                      <Tabs.TabPane
                        tab={
                          <div className="sub-tab-title">
                            <FeatherIcon icon="users" size={16} />
                            <span>In-House Guests</span>
                            <span className="sub-tab-count">{inHouse?.length || 0}</span>
                          </div>
                        }
                        key="inhouse"
                      >
                        {canViewInHouse ? (
                          <AdvancedTable
                            resource="inhouse"
                            columns={inHouseColumns}
                            fetchData={fetchInHouseData}
                            enableSearch={false}
                            enableFilters={false}
                            defaultPageSize={10}
                          />
                        ) : (
                          <div className="permission-denied-message">
                            You do not have permission to view in-house guests.
                          </div>
                        )}
                      </Tabs.TabPane>

                      <Tabs.TabPane
                        tab={
                          <div className="sub-tab-title">
                            <FeatherIcon icon="log-in" size={16} />
                            <span>Today&apos;s Check-ins</span>
                            <span className="sub-tab-count">{checkIns?.length || 0}</span>
                          </div>
                        }
                        key="checkins"
                      >
                        {canViewCheckIn ? (
                          <AdvancedTable
                            resource="checkins"
                            columns={checkInColumns}
                            fetchData={fetchCheckInsData}
                            enableSearch={false}
                            enableFilters={false}
                            defaultPageSize={10}
                          />
                        ) : (
                          <div className="permission-denied-message">
                            You do not have permission to view check-ins.
                          </div>
                        )}
                      </Tabs.TabPane>
                      <Tabs.TabPane
                        tab={
                          <div className="sub-tab-title">
                            <FeatherIcon icon="log-out" size={16} />
                            <span>Today&apos;s Check-outs</span>
                            <span className="sub-tab-count">{checkOuts?.length || 0}</span>
                          </div>
                        }
                        key="checkouts"
                      >
                        {canViewCheckOut ? (
                          <AdvancedTable
                            resource="checkouts"
                            columns={checkOutColumns}
                            fetchData={fetchCheckOutsData}
                            enableSearch={false}
                            enableFilters={false}
                            defaultPageSize={10}
                          />
                        ) : (
                          <div className="permission-denied-message">
                            You do not have permission to view check-outs.
                          </div>
                        )}
                      </Tabs.TabPane>

                      <Tabs.TabPane
                        tab={
                          <div className="sub-tab-title">
                            <FeatherIcon icon="activity" size={16} />
                            <span>Medi-Daycare Rituals</span>
                            <span className="sub-tab-count">{mediDaycareCount || 0}</span>
                          </div>
                        }
                        key="medidaycare"
                      >
                        {canViewMediDaycare ? (
                          <AdvancedTable
                            resource="medidaycare"
                            columns={mediDaycareCheckInColumns}
                            fetchData={fetchMediDaycareCheckIn}
                            enableSearch={false}
                            enableFilters={false}
                            defaultPageSize={10}
                          />
                        ) : (
                          <div className="permission-denied-message">
                            You do not have permission to view medi-daycare rituals.
                          </div>
                        )}
                      </Tabs.TabPane>
                    </Tabs>
                  </div>
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane tab={tabTimeline} key="2">
                <div className="dashboard-overview-container">
                  {canViewScheduler ? (
                    <>
                      <div className="dashboard-header-row mb-4">
                        <DatePicker.RangePicker
                          value={[
                            timelineRange.startDate ? dayjs(timelineRange.startDate) : null,
                            timelineRange.endDate ? dayjs(timelineRange.endDate) : null
                          ]}
                          onChange={handleTimelineDateRangeChange}
                          placeholder={['Start Date', 'End Date']}
                          style={{ width: '100%', height: '42px', maxWidth: '400px' }}
                          allowClear={true}
                        />
                      </div>

                      <TimelineScheduler
                        filterStartDate={timelineRange.startDate}
                        filterEndDate={timelineRange.endDate}
                      />
                    </>
                  ) : (
                    <div className="permission-denied-message">
                      You do not have permission to view the scheduler.
                    </div>
                  )}
                </div>
              </Tabs.TabPane>
            </Tabs>
            {loader && (
              <div className="loader-container">
                <Spin size="large" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RoomDashboard;
